import React, { useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useExperimentGatedAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { useMaybePlayAudioOnceOnMount } from 'features/app-takeover/utils/play-audio-once';
import { MenuReorderWidget } from 'features/menu/components/menu-reorder-widget';
import MenuTileGrid from 'features/menu/components/menu-tile-grid';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import withMenuEmptyState from 'features/menu/components/with-menu-empty-state';
import { useFetchMainMenu, useFetchMainMenuOnMount } from 'features/menu/hooks/use-fetch-main-menu';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { MenuEmptyState } from 'features/menu/pages/main-menu-page/menu-empty-state';
import { excludeDisabledMenuSections } from 'features/menu/pages/main-menu-page/utils';
import { useStoreContext } from 'features/store';

import { IMainMenuViewOption } from '@rbi-ctg/menu';
import { Helmet } from 'components/helmet';
import { usePullToReload } from 'components/pull-to-reload/pull-to-reload';
import { asPreviewMenuEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/as-preview-menu-entry';
import { useOneMenuCatalog } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import { useUnavailableItemId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-unavailable-item-id';
import { useUnavailableSectionId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-unavailable-section-id';
import {
  buildLegacyFullMenuRecoveryData,
  createLegacyMenuObjectFromCatalogEntry,
} from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/build-legacy-full-menu-recovery-data';
import { useDeeplinksFlowEnabled } from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { useNativeTabBarContentInset } from 'hooks/use-native-tab-bar-content-inset';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { useMenuContext } from 'state/menu';
import { useServiceModeLive } from 'state/service-mode';
import { routes } from 'utils/routing';
import { waitForDuration } from 'utils/wait-for-duration';

// OLD legacy menu
const MainMenuComponent = () => {
  const router = useRouter();
  const fetchMainMenu = useFetchMainMenu();
  const { store } = useStoreContext();
  const { language } = useLocale();
  const isDeeplinksFlowEnabled = useDeeplinksFlowEnabled();
  const unavailableItemIdParam = useUnavailableItemId();
  const unavailableSectionIdParam = useUnavailableSectionId();
  const treatmentUnavailableItemId = isDeeplinksFlowEnabled ? unavailableItemIdParam : undefined;
  const treatmentUnavailableSectionId = isDeeplinksFlowEnabled
    ? unavailableSectionIdParam
    : undefined;
  const needsCatalog = Boolean(treatmentUnavailableItemId || treatmentUnavailableSectionId);
  const {
    entries: catalogEntries,
    findEntry: findCatalogEntry,
    loading: catalogLoading,
  } = useOneMenuCatalog({ skip: !needsCatalog });

  const serviceMode = useServiceModeLive();
  const {
    activeDayParts,
    allPricingDependenciesAreSettled,
    forceRefreshActiveDayParts,
    showBurgersForBreakfast,
    hasError,
    data,
    getPricingAndAvailability,
    isMenuLoading,
  } = useMenuContext();

  const menuData = excludeDisabledMenuSections({
    menuData: data?.Menu?.options as IMainMenuViewOption[] | undefined,
    activeDayParts,
    showBurgersForBreakfast,
    serviceMode,
    getPricingAndAvailability,
  });

  const recovery = useMemo(() => {
    if (!treatmentUnavailableItemId && !treatmentUnavailableSectionId) {
      return null;
    }

    const resolveCatalogEntry = (entryId: string) => {
      const entry =
        findCatalogEntry(entryId) ?? catalogEntries.find(candidate => candidate.id === entryId);
      return entry ? asPreviewMenuEntry(entry, language) : undefined;
    };
    const unavailableItemEntry = treatmentUnavailableItemId
      ? resolveCatalogEntry(treatmentUnavailableItemId)
      : undefined;
    const legacyMainMenuSections = (data?.Menu?.options ?? []) as IMainMenuViewOption[];
    const unavailableSectionFromLegacyMenu = treatmentUnavailableSectionId
      ? legacyMainMenuSections.find(section => section._id === treatmentUnavailableSectionId)
      : undefined;
    const unavailableSectionEntry =
      treatmentUnavailableSectionId && !unavailableSectionFromLegacyMenu
        ? resolveCatalogEntry(treatmentUnavailableSectionId)
        : undefined;

    return buildLegacyFullMenuRecoveryData({
      availableSections: menuData ?? [],
      unavailableItem: unavailableItemEntry
        ? createLegacyMenuObjectFromCatalogEntry(unavailableItemEntry)
        : undefined,
      unavailableSection: unavailableSectionFromLegacyMenu
        ? (unavailableSectionFromLegacyMenu as IMainMenuViewOption)
        : unavailableSectionEntry
        ? createLegacyMenuObjectFromCatalogEntry(unavailableSectionEntry)
        : undefined,
    });
  }, [
    catalogEntries,
    data?.Menu?.options,
    findCatalogEntry,
    language,
    menuData,
    treatmentUnavailableItemId,
    treatmentUnavailableSectionId,
  ]);

  const handleUnavailableChooseRestaurant = useCallback(() => {
    if (!treatmentUnavailableItemId || recovery?.kind !== 'item') {
      return;
    }
    const entry =
      findCatalogEntry(treatmentUnavailableItemId) ??
      catalogEntries.find(candidate => candidate.id === treatmentUnavailableItemId);
    const typeSegment = String(entry?.type ?? 'item').toLowerCase();
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: {
        redirectUrl: `${routes.menu}/${typeSegment}/${treatmentUnavailableItemId}`,
        unavailableItemId: treatmentUnavailableItemId,
      },
    });
  }, [catalogEntries, findCatalogEntry, recovery?.kind, router, treatmentUnavailableItemId]);

  const onRefresh = async (): Promise<void> => {
    // This is just an animation feedback of one second
    await waitForDuration(1000);
    forceRefreshActiveDayParts();
    fetchMainMenu(true);
  };

  useMenuScroll();

  const { isPosDataLoading } = useStoreContext();
  const isFetchMainMenuPending = useFetchMainMenuOnMount();
  const loading =
    isFetchMainMenuPending ||
    isMenuLoading ||
    isPosDataLoading ||
    (!data && !hasError) ||
    (needsCatalog && catalogLoading);

  const displayedMenuData = recovery?.data ?? menuData;
  const hasMenuData = !!displayedMenuData?.length && allPricingDependenciesAreSettled;

  const pullToReloadProps = usePullToReload({
    onRefresh,
    isDisabled: loading,
  });
  const nativeTabBarContentInset = useNativeTabBarContentInset();

  useMaybePlayAudioOnceOnMount(useExperimentGatedAppTakeover()?.menu?.audio, {
    shouldPlay: hasMenuData && !hasError,
    globalPlayOnce: true,
  });

  const menuContent =
    hasMenuData && !hasError ? (
      <MenuTileGrid
        data={displayedMenuData}
        paddingBottom={nativeTabBarContentInset}
        deeplinkUnavailableItemId={recovery?.deeplinkUnavailableItemId}
        onUnavailableChooseRestaurant={
          recovery?.kind === 'item' ? handleUnavailableChooseRestaurant : undefined
        }
        {...pullToReloadProps}
      />
    ) : (
      <MenuEmptyState
        onRefresh={onRefresh}
        logWhenTrue={!loading}
        logContext={{
          menuType: 'legacy',
          activeDayParts: activeDayParts.map(dayPart => dayPart.key),
          showBurgersForBreakfast,
          serviceMode,
          isFetchedMenuEmpty: !hasMenuData,
          originalMenuDataLength: data?.Menu?.options?.length,
          finalMenuDataLength: displayedMenuData?.length,
          isPricingAndDependenciesSettled: allPricingDependenciesAreSettled,
          storeId: store.number,
          storePosVendor: store.pos?.vendor,
          hasError,
          isMenuLoading,
        }}
      />
    );

  return (
    <>
      <Helmet title={t('mainMenu')} />
      {loading ? (
        <MenuSkeletonLoader />
      ) : (
        <>
          <MenuReorderWidget />
          {menuContent}
        </>
      )}
    </>
  );
};

// MainMenu.whyDidYouRender = true;
export const MainMenuPage = withMenuEmptyState(MainMenuComponent);
